import React from 'react';
import { connect } from 'react-redux';
import {
	Shopper,
	OrderInfo,
	ProductApi,
	OrderProduct,
	PartialOrder,
	PartialWithId,
	OrderObject,
} from 'common/types';
import { updateActivePartialOrder } from 'actions';
import { Theme } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { getNextProductReturnTime } from 'common/utils/dateCalculations';
import { useTranslation } from 'services/localization/useTranslation';
import { getProductReturnProps, getProductUnreturnProps } from 'common/utils/rentalUtils';

interface OwnProps {
	rentalInfo: OrderInfo;
	shopper: Shopper;
	shopperProducts: OrderProduct[];
	stockProducts: ProductApi[];
	rental: OrderObject;
}

interface DispatchProps {
	updateActivePartialOrder: (order: PartialOrder) => void;
}

type Props = OwnProps & DispatchProps & WithStyles<typeof styles>;

export const ShopperReturnInformation = ({ ...props }: Props) => {
	const { rentalInfo, shopper, shopperProducts, classes, rental } = props;
	const { activeState, rentalState } = rentalInfo;
	const { t } = useTranslation();

	const handleReturnedSwitch = (e: React.ChangeEvent<HTMLInputElement>) => {
		const returned = e.target.checked;
		const returnTime = new Date().toISOString();
		const updatedPartialShopper: Partial<Shopper> = {
			productsReturned: returned,
		};
		const partialShoppers: PartialWithId<Shopper>[] = [
			{
				id: shopper.id,
				data: updatedPartialShopper,
			},
		];
		let partialProducts: PartialWithId<OrderProduct>[] = [];
		let partialRentalInfo: PartialWithId<OrderInfo> | undefined;
		const shopperProductIds = shopperProducts.map((p) => p.id);
		const notReturnedOtherItems = rental.products.filter(
			(product) => !shopperProductIds.includes(product.id) && product.endDateReturned === null,
		);
		const returnTimeNext = getNextProductReturnTime(notReturnedOtherItems);
		if (returned) {
			partialProducts = shopperProducts.map((product) => ({
				id: product.id,
				data: getProductReturnProps(product, returnTime),
			}));
			let updatedRentalInfo: Partial<OrderInfo> = {
				returnTimeNext,
				endDateReturned: notReturnedOtherItems.length === 0 ? new Date().toISOString() : null,
			};
			if (notReturnedOtherItems.length === 0) {
				updatedRentalInfo = {
					...updatedRentalInfo,
					activeState: 'RENT_ENDED',
					rentalState: 'COMPLETED',
					endDateReturned: new Date().toISOString(),
				};
			}
			partialRentalInfo = {
				id: rentalInfo.id,
				data: updatedRentalInfo,
			};
		} else {
			partialProducts = shopperProducts.map((product) => ({
				id: product.id,
				data: getProductUnreturnProps(product),
			}));
			let returnTimeNext = getNextProductReturnTime([...notReturnedOtherItems, ...shopperProducts]);
			const updatedRentalInfo: Partial<OrderInfo> = {
				returnTimeNext,
			};
			partialRentalInfo = {
				id: rentalInfo.id,
				data: updatedRentalInfo,
			};
		}
		const partialOrder: PartialOrder = {
			rentalInfo: partialRentalInfo,
			products: partialProducts,
			shoppers: partialShoppers,
		};
		props.updateActivePartialOrder(partialOrder);
	};

	const renderReturnSwitch = () => {
		return (
			<FormGroup>
				<FormControlLabel
					control={
						<Switch
							color="primary"
							id={shopper.firstName}
							checked={Boolean(shopper.productsReturned)}
							onChange={handleReturnedSwitch}
							disabled={rentalState === 'COMPLETED'}
						/>
					}
					label={
						rentalState === 'COMPLETED' || Boolean(shopper.productsReturned)
							? t('common:actions.ended', 'Ended')
							: t('orderActions.endOrder', 'End order')
					}
				/>
			</FormGroup>
		);
	};

	return (
		<React.Fragment>
			{activeState !== 'STORE_BOOKED' &&
			activeState !== 'ONLINE_BOOKED' &&
			activeState !== 'ADMIN_BOOKED' &&
			activeState !== 'API_BOOKED' ? (
				<div className={classes.returnTime}>{renderReturnSwitch()}</div>
			) : null}
		</React.Fragment>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		returnTimeContainer: {
			margin: '0px 0px 16px 0px',
		},
		returnTime: {
			display: 'flex',
			alignItems: 'flexStart',
			justifyContent: 'space-between',
			flexGrow: 1,
			flexWrap: 'wrap',
			minWidth: '115px',
		},
		durationContainer: {
			display: 'flex',
		},
		flexGrow: {
			flexGrow: 1,
		},
		editBtn: {
			marginTop: '5px',
			padding: 0,
		},
		textField: {
			minWidth: '200px',
			margin: '16px 16px 0px 0px',
		},
		saveBtn: {
			marginTop: '16px',
		},
		menu: {
			width: 200,
		},
	});

const dispatchProps: DispatchProps = {
	updateActivePartialOrder,
};

export default withStyles(styles)(connect(null, dispatchProps)(ShopperReturnInformation));
